import { Modal, Tag } from 'antd'
import Table, { ColumnGroupType, ColumnsType, ColumnType } from 'antd/es/table'
import CheckableTag from 'antd/es/tag/CheckableTag'
import { defaultSortedColumn } from 'features/fde/helpers/tableHelpers'
import { Sub } from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  FfsParameterField,
  FfsParameterInputFieldProps,
} from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { useGeckoLocale } from 'features/locale'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import type { AllowableStressRec, StressTempF } from './allowableStressValues'
import { STRESS_TEMPS_F } from './allowableStressValues'

const TableFooterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: right;
`

const LABEL_YIELD = (
  <>
    S<Sub>y</Sub>
  </>
)
const LABEL_TENSILE = (
  <>
    S<Sub>t</Sub>
  </>
)

export interface MinTensileStrengthTableProps {
  /** Material catalog to display. Required — callers resolve it per component type. */
  records: readonly AllowableStressRec[]
  /**
   * Controlled allowable-stress value. `undefined` = UNCONTROLLED: the table owns
   * its highlight and reports clicks via `onValueClicked` only (e.g. the Libra
   * picker). `null` = controlled-but-empty. A real value = controlled.
   */
  stressValue?: GeckoMeasurement | null
  setStressValue?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  tensileStrength?: GeckoMeasurement | null
  setTensileStrength?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  /**
   * Fired with the clicked cell's value on a genuine user click. A consumer can
   * pass its field setter here directly to write the clicked S into a field
   * without wiring the controlled `stressValue`/`setStressValue` pair.
   */
  onValueClicked?: (value: GeckoMeasurement) => void
}

function formatStrength(
  value: GeckoMeasurement | null,
  unit: GeckoMeasurementUnit
): string {
  return (
    value
      ?.convertTo(unit)
      .num.toFixed(unit === GeckoMeasurementUnit.Megapascals ? 2 : 0) ?? ''
  )
}

// The temperature columns (StressTempF) are design temperatures in Fahrenheit
// (ASME B31.3-2022 Table A-1). Convert the header label to the preferred
// temperature unit; the numeric key that indexes the data is unchanged.
function formatTempHeader(
  temp: StressTempF,
  unit: GeckoMeasurementUnit
): string {
  const converted = GeckoMeasurement.CreateFromValue(
    Number(temp),
    GeckoMeasurementUnit.Fahrenheit
  ).convertTo(unit).num
  return `${converted.toFixed(unit === GeckoMeasurementUnit.Fahrenheit ? 0 : 1)}${unit.name}`
}

const RawStressTag: FC<{
  value: GeckoMeasurement
  selected: boolean
  select: () => void
  unit: GeckoMeasurementUnit
}> = ({ value, selected, select, unit }) => {
  return (
    <Tag.CheckableTag
      checked={selected}
      onChange={(checked) => {
        if (checked) {
          select()
        }
      }}>
      {formatStrength(value, unit)}
    </Tag.CheckableTag>
  )
}
RawStressTag.displayName = 'StressTag'
const StressTag = React.memo(RawStressTag)

export const MinTensileStrengthTable: FC<MinTensileStrengthTableProps> = ({
  records,
  stressValue,
  setStressValue,
  tensileStrength,
  setTensileStrength,
  onValueClicked,
}) => {
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [selectedTemp, setSelectedTemp] = useState<StressTempF | null>(null)

  // Both units follow the single global measurement system — no local toggle.
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const strengthUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.Megapascals
  )
  const tempHeaderUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.Fahrenheit
  )

  // A new dataset invalidates any prior selection.
  useEffect(() => {
    setSelectedRowId(null)
    setSelectedTemp(null)
  }, [records])

  // Temperature columns present in this dataset, in canonical numeric order.
  const temperatures = useMemo(
    () =>
      STRESS_TEMPS_F.filter((t) =>
        records.some((r) => r.allowableStress[t] !== undefined)
      ),
    [records]
  )

  const isControlled = stressValue !== undefined

  const selectedValue = useMemo<GeckoMeasurement | null>(() => {
    if (!selectedRowId || selectedTemp === null) return null
    const row = records.find((r) => r.sourceId === selectedRowId)
    return row?.allowableStress[selectedTemp] ?? null
  }, [records, selectedRowId, selectedTemp])

  // Controlled consumers (e.g. MAWP): if the external value diverges from the
  // highlighted cell, drop the highlight. Uncontrolled consumers (Libra passes
  // no `stressValue`) never clear on this basis, so a click's highlight sticks.
  useEffect(() => {
    if (!isControlled) return
    if (
      !GeckoMeasurement.Equals(
        stressValue ?? undefined,
        selectedValue ?? undefined
      )
    ) {
      setSelectedRowId(null)
      setSelectedTemp(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stressValue])

  const handleSelect = useCallback(
    (rec: AllowableStressRec, temp: StressTempF, value: GeckoMeasurement) => {
      setSelectedRowId(rec.sourceId)
      setSelectedTemp(temp)
      setStressValue?.(value)
      // Picking S also seeds the row's tensile for controlled tensile consumers.
      setTensileStrength?.(rec.minTensile)
      onValueClicked?.(value)
    },
    [setStressValue, setTensileStrength, onValueClicked]
  )

  const columns: ColumnsType<AllowableStressRec> = useMemo(() => {
    const tempChildren: ColumnType<AllowableStressRec>[] = temperatures.map(
      (temp) => ({
        title: formatTempHeader(temp, tempHeaderUnit),
        dataIndex: ['allowableStress', temp],
        key: `s-${temp}`,
        width: 66,
        render: (value: GeckoMeasurement | undefined, rec) => {
          // Sparse: cells above the material's max temp render blank & non-clickable.
          if (!value) return null
          const selected =
            rec.sourceId === selectedRowId && selectedTemp === temp
          return (
            <StressTag
              unit={strengthUnit}
              value={value}
              selected={selected}
              select={() => handleSelect(rec, temp, value)}
            />
          )
        },
      })
    )

    return [
      {
        ...defaultSortedColumn<AllowableStressRec>('Material', 'material'),
        fixed: 'left',
        width: 150,
      },
      {
        ...defaultSortedColumn<AllowableStressRec>('Spec. No.', 'spec'),
        fixed: 'left',
        width: 90,
      },
      {
        ...defaultSortedColumn<AllowableStressRec>('Type / Grade', 'grade'),
        fixed: 'left',
        width: 100,
      },
      {
        title: 'Class',
        dataIndex: 'materialClass',
        key: 'materialClass',
        width: 64,
        render: (v: string | null) => v ?? '—',
      },
      {
        ...defaultSortedColumn<AllowableStressRec>(
          'Min Tensile',
          'minTensile',
          (value: GeckoMeasurement, row) =>
            tensileStrength !== undefined ? (
              <CheckableTag
                checked={
                  selectedRowId === row.sourceId &&
                  tensileStrength?.toNumber(
                    GeckoMeasurementUnit.Megapascals
                  ) ===
                    row.minTensile.toNumber(GeckoMeasurementUnit.Megapascals)
                }
                onChange={(checked: boolean) => {
                  if (checked && setTensileStrength) {
                    setTensileStrength(row.minTensile)
                    setSelectedRowId(row.sourceId)
                  }
                }}>
                {formatStrength(value, strengthUnit)}
              </CheckableTag>
            ) : (
              formatStrength(value, strengthUnit)
            )
        ),
        width: 110,
      },
      {
        title: 'Min Yield',
        dataIndex: 'minYield',
        key: 'minYield',
        width: 90,
        render: (value: GeckoMeasurement | null) =>
          value ? formatStrength(value, strengthUnit) : '—',
      },
      {
        title: `Allowable Stress S @ Metal Temp (${tempHeaderUnit.name}) — click one`,
        children: tempChildren,
      } as ColumnGroupType<AllowableStressRec>,
    ]
  }, [
    temperatures,
    selectedRowId,
    selectedTemp,
    strengthUnit,
    tempHeaderUnit,
    tensileStrength,
    setTensileStrength,
    handleSelect,
  ])

  return (
    <Table
      columns={columns}
      dataSource={records}
      rowKey={(rec) => rec.sourceId}
      pagination={false}
      size='small'
      scroll={{ y: '40rem', x: 'max-content' }}
      footer={() => (
        <TableFooterContainer>
          Stress unit: {strengthUnit.name}
        </TableFooterContainer>
      )}
    />
  )
}

export const YieldStrengthParameterField: FC<
  Omit<MinTensileStrengthTableProps, 'records' | 'onValueClicked'> & {
    /** Nullable at this level: when there's no dataset the lookup is suppressed. */
    records: readonly AllowableStressRec[] | null
  } & Pick<FfsParameterInputFieldProps, 'labelWidth' | 'caption'>
> = ({
  records,
  stressValue,
  setStressValue,
  tensileStrength,
  setTensileStrength,
  labelWidth,
  caption,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const recs = records ?? []
  const hasLookup = recs.length > 0
  return (
    <>
      {hasLookup && (
        <Modal
          title='Pick an allowable stress (S) — ASME B31.3 Table A-1'
          forceRender={true}
          onCancel={() => setOpen(false)}
          onOk={() => setOpen(false)}
          footer={<></>}
          open={open}
          width={1000}>
          <MinTensileStrengthTable
            records={recs}
            stressValue={stressValue}
            setStressValue={setStressValue}
            tensileStrength={tensileStrength}
            setTensileStrength={setTensileStrength}
          />
        </Modal>
      )}
      <FfsParameterField
        label={LABEL_YIELD}
        labelTooltip='Stress Value'
        measurementType='pressure'
        labelWidth={labelWidth}
        value={stressValue ?? null}
        setValue={setStressValue}
        caption={caption}
        onLookup={hasLookup ? () => setOpen(true) : undefined}
      />
      {tensileStrength !== undefined ? (
        <FfsParameterField
          label={LABEL_TENSILE}
          labelTooltip='Tensile Strength'
          measurementType='pressure'
          labelWidth={labelWidth}
          value={tensileStrength}
          setValue={setTensileStrength}
          onLookup={hasLookup ? () => setOpen(true) : undefined}
        />
      ) : undefined}
    </>
  )
}
